'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { Download, Smartphone, AlertTriangle, ChevronRight, Wifi } from 'lucide-react';
import { APK_API_DOWNLOAD_PATH } from '@/lib/apk-install';
import { useBrand } from '@/components/providers/BrandProvider';

type Props = {
  apkReady: boolean;
  apkSizeMb?: string;
  siteOrigin?: string;
};

export default function IdiegtiClient({ apkReady, apkSizeMb, siteOrigin }: Props) {
  const brand = useBrand();
  const origin =
    typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : siteOrigin ?? '';
  const downloadUrl = origin ? `${origin}${APK_API_DOWNLOAD_PATH}` : APK_API_DOWNLOAD_PATH;

  const isLanDev = useMemo(
    () =>
      origin.includes('192.168.') ||
      origin.includes('10.') ||
      origin.includes('localhost') ||
      origin.includes('127.0.0.1'),
    [origin]
  );

  const serverLabel = origin.includes('imre2.indevi.eu')
    ? 'imre2.indevi.eu'
    : origin.replace(/^https?:\/\//, '') || 'jūsų serveris';

  return (
    <div className="min-h-[100dvh] bg-stone-100 flex flex-col items-center justify-center p-5">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-6">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-stone-900 flex items-center justify-center mb-4">
          <Smartphone className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-xl font-bold text-stone-900 text-center">{brand.appName} Android</h1>
        <p className="text-sm text-slate-500 mt-2 mb-5 text-center">
          Atsisiųskite ir įsidiekite programėlę planšetėje. Po diegimo atidarykite piktogramą —
          ne naršyklę.
        </p>

        {isLanDev && apkReady && (
          <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            <div className="flex items-start gap-2 font-semibold mb-1">
              <Wifi className="w-4 h-4 shrink-0" />
              Vietinis tinklas
            </div>
            <p>Programėlė jungsis į šį adresą:</p>
            <p className="font-mono mt-1 break-all text-[11px]">{origin || serverLabel}</p>
          </div>
        )}

        {!apkReady && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
            APK failas dar neparuoštas. Kompiuteryje paleiskite:{' '}
            <span className="font-mono font-semibold">npm run apk:build:local</span>
          </div>
        )}

        <a
          href={apkReady ? downloadUrl : undefined}
          className={`flex items-center justify-center gap-2 w-full py-4 rounded-2xl text-white text-sm font-bold active:scale-[0.98] transition-all ${
            apkReady ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 pointer-events-none'
          }`}
          aria-disabled={!apkReady}
        >
          <Download className="w-5 h-5" />
          {apkReady ? `Atsisiųsti ${brand.appName} (${apkSizeMb} MB)` : `Atsisiųsti ${brand.appName} (.apk)`}
        </a>

        {apkReady && (
          <p className="mt-2 text-[11px] text-slate-500 text-center break-all">
            Tiesioginė nuoroda:{' '}
            <a href={downloadUrl} className="text-emerald-700 underline">
              {downloadUrl}
            </a>
          </p>
        )}

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
          <div className="flex items-start gap-2 font-semibold">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Jei turite seną programą — ištrinkite ją prieš diegdami {brand.appName}.</span>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold text-stone-800 mb-2">Samsung / Android planšetė</p>
          <ol className="text-xs text-slate-600 space-y-2 list-decimal list-inside">
            <li>Spauskite žalią mygtuką — atsisiųs APK failas.</li>
            <li>
              Jei klausia leidimo: <span className="font-medium">Nustatymai → Saugumas</span> —
              leiskite diegti iš šio šaltinio / nežinomų programų.
            </li>
            <li>Atidarykite atsisiųstą failą → <span className="font-medium">Įdiegti</span>.</li>
            <li>Leiskite vietą (GPS) ir kamerą.</li>
          </ol>
        </div>

        <p className="text-[11px] text-slate-400 mt-4 text-center">
          Programėlė jungiasi į <span className="font-mono">{serverLabel}</span>
          {!origin.includes('imre2.indevi.eu') && (
            <> (gamyba: imre2.indevi.eu)</>
          )}
        </p>

        <Link
          href="/login"
          className="mt-4 flex items-center justify-center gap-1 w-full py-2.5 text-xs text-slate-500 hover:text-stone-800"
        >
          Tęsti naršyklėje <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
